import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import Categories from "@/pages/Categories";
import Salarios from "@/pages/Salarios";
import Products from "@/pages/Products";
import Inventario from "@/pages/Inventario";
import Vendors from "@/pages/Vendors";
import VendorsPrint from "@/pages/VendorsPrint";
import Carta from "@/pages/Carta";
import Albaranes from "@/pages/Albaranes";
import Settings from "@/pages/Settings";
import Budget from "@/pages/Budget";
import Admin from "@/pages/Admin";
import Deudas from "@/pages/Deudas";
import Treasury from "@/pages/Treasury";
import Landing from "@/pages/Landing";
import Layout from "@/components/Layout";
import FeatureGate from "@/components/FeatureGate";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[var(--text-muted)]">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function MasterOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!["master", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/ingresos" element={<Invoices mode="income" />} />
              <Route path="/salarios" element={<Salarios />} />
              <Route path="/salarios-lista" element={<Invoices mode="salaries" />} />
              <Route path="/products" element={<Products />} />
              <Route path="/inventario" element={<FeatureGate feature="inventory"><Inventario /></FeatureGate>} />
              <Route path="/proveedores" element={<Vendors />} />
              <Route path="/proveedores/imprimir" element={<VendorsPrint />} />
              <Route path="/carta" element={<FeatureGate feature="menu"><Carta /></FeatureGate>} />
              <Route path="/albaranes" element={<Albaranes />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/presupuesto" element={<FeatureGate feature="budget"><Budget /></FeatureGate>} />
              <Route path="/deudas" element={<FeatureGate feature="client_debts"><Deudas /></FeatureGate>} />
              <Route path="/tesoreria" element={<FeatureGate feature="treasury"><Treasury /></FeatureGate>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin" element={<MasterOnly><Admin /></MasterOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
